import React, { useMemo, useState } from "react";
import { Form, Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import FloatingLabelInput from "react-floating-label-input";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import TranslateIcon from "@mui/icons-material/Translate";
import { SignInImages } from "../config/imagesLink";
import WarningToast from "./WarningToast";
import ErrorToast from "./ErrorToast";
import SuccessToast from "./SuccessToast";
import axios, { PublicKey } from "../API/axios";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import i18n from "../i18n";
import {
  getLangKey,
  getStoredLangCode,
  setStoredLangCode,
} from "../utils/language";

/**
 * SPA password reset — calls API OAuth/SetNewPassword with UserId + Token from query.
 * Reset emails should link to /SetPassword?UserId=...&Token=...
 */
const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const userId = useMemo(
    () => searchParams.get("UserId") || searchParams.get("userId") || "",
    [searchParams],
  );
  const token = useMemo(
    () => searchParams.get("Token") || searchParams.get("token") || "",
    [searchParams],
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [warningToastMessage, setWarningToastMessage] = useState("");
  const [showWarningToast, setShowWarningToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);

  const language = getStoredLangCode();

  const changeLanguage = (lang) => {
    const code = setStoredLangCode(lang);
    i18n.changeLanguage(code);
  };

  const LinktoGuest = () => {
    localStorage.setItem("guestMode", "true");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowToast(false);
    setShowWarningToast(false);
    setShowErrorToast(false);

    if (!userId || !token) {
      setErrorToastMessage(
        t("SignIn.InvalidResetLink", {
          defaultValue: "Invalid or missing password reset link.",
        }),
      );
      setShowErrorToast(true);
      return;
    }
    if (!password || password.length < 6) {
      setWarningToastMessage(
        t("SignIn.PasswordTooShort", {
          defaultValue: "Password must be at least 6 characters.",
        }),
      );
      setShowWarningToast(true);
      return;
    }
    if (password !== confirmPassword) {
      setWarningToastMessage(
        t("SignIn.PasswordMismatch", {
          defaultValue: "New password and confirm password do not match.",
        }),
      );
      setShowWarningToast(true);
      return;
    }

    setLoading(true);
    try {
      const langKey = getLangKey(language);
      const res = await axios.post(
        `OAuth/SetNewPassword?UserId=${encodeURIComponent(userId)}&Token=${encodeURIComponent(token)}&LangKey=${encodeURIComponent(langKey)}`,
        { newPassword: password },
        { headers: { PublicKey } },
      );
      setToastMessage(res?.data?.message || "Password Successfully Changed.");
      setShowToast(true);
      setTimeout(() => navigate("/SignIn"), 1500);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        t("SignIn.ResetFailed", {
          defaultValue: "Unable to reset password. Link may be expired.",
        });
      if (error?.response?.status === 404) {
        setWarningToastMessage(msg);
        setShowWarningToast(true);
      } else {
        setErrorToastMessage(msg);
        setShowErrorToast(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .language-dropdown .dropdown-menu {
          transform: none !important;
          inset: auto !important;
          right: 10px !important;
          bottom: 40px !important;
        }
      `}</style>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-8 full-height p-2 loginImage">
            <div className="bg-cover">
              <div className="imageData">
                <div className="text-center">
                  <img src={SignInImages.whiteLogo} alt="" />
                </div>
                <p>
                  {t("SignIn.Stay connected for")}{" "}
                  <NavLink
                    to={`/SearchJob`}
                    onClick={LinktoGuest}
                    className="border-bottom"
                  >
                    {t("Company Profile.Jobs")}
                  </NavLink>{" "}
                  {t("SignIn.and")}{" "}
                  <NavLink
                    to={`/NewsFeed`}
                    onClick={LinktoGuest}
                    className="border-bottom"
                  >
                    {t("SignIn.More")}
                  </NavLink>
                </p>
              </div>
              <div className="linksHelp">
                <p>
                  <a href="/privacy-policy.html" target="_blank" rel="noreferrer">
                    {t("Sidemenubar.Privacy Policy")}
                  </a>{" "}
                  |{" "}
                  <a
                    href="/terms-and-conditions.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Sidemenubar.Terms & Conditions")}
                  </a>
                </p>
                <p className="d-flex">
                  {t("SignIn.Having Troubles")}?{" "}
                  <a
                    href="/faq.html"
                    className="border-bottom me-3"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("SignIn.Get Help")}
                  </a>
                  <Dropdown className="language-dropdown">
                    <Dropdown.Toggle
                      id="language-toggle"
                      variant="link"
                      className="text-decoration-none p-0 d-flex align-items-center language-btn"
                    >
                      <TranslateIcon
                        className="material-symbols-sharp me-3"
                        style={{ left: "20%", fill: "white" }}
                      />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item as="button" onClick={() => changeLanguage("en")}>
                        En
                      </Dropdown.Item>
                      <Dropdown.Item as="button" onClick={() => changeLanguage("hi")}>
                        Hi
                      </Dropdown.Item>
                      <Dropdown.Item as="button" onClick={() => changeLanguage("ar")}>
                        Ar
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </p>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6 Login-Section">
            <div className="form-container pb-3">
              <h5 className="mb-3">
                {t("SignIn.SetPassword", { defaultValue: "Set new password" })}
              </h5>
              <Form onSubmit={handleSubmit} className="formBorder p-1">
                <div className="password-input border-signIn d-flex align-items-baseline">
                  <FloatingLabelInput
                    id="new-password"
                    label={t("SignIn.Password")}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </div>
                </div>
                <div className="password-input border-signIn d-flex align-items-baseline mt-2">
                  <FloatingLabelInput
                    id="confirm-password"
                    label={t("SignIn.ConfirmPassword", {
                      defaultValue: "Confirm password",
                    })}
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <div
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-100 mt-3"
                  disabled={loading}
                >
                  {loading
                    ? t("SignIn.PleaseWait", { defaultValue: "Please wait..." })
                    : t("SignIn.SavePassword", { defaultValue: "Save password" })}
                </button>
                <p className="mt-3 text-center">
                  <Link to="/SignIn">{t("SignIn.BackToSignIn", { defaultValue: "Back to Sign In" })}</Link>
                </p>
              </Form>
            </div>
          </div>
        </div>
      </div>

      <SuccessToast
        show={showToast}
        message={toastMessage}
        onClose={() => setShowToast(false)}
      />
      <WarningToast
        show={showWarningToast}
        message={warningToastMessage}
        onClose={() => setShowWarningToast(false)}
      />
      <ErrorToast
        show={showErrorToast}
        message={errorToastMessage}
        onClose={() => setShowErrorToast(false)}
      />
    </>
  );
};

export default SetPassword;
